import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  MoreVert,
  EditOutlined,
  ArchiveOutlined,
  UnarchiveOutlined,
  DirectionsCarFilledOutlined,
  TwoWheelerOutlined,
  ChevronRight,
  SpeedOutlined,
  CurrencyRupeeOutlined,
  InsightsOutlined,
} from '@mui/icons-material';
import { useSupabase } from '../../contexts/SupabaseContext';
import { VEHICLE_TYPES } from '../../utils/fuelEfficiency';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';

const emptyForm = { name: '', vehicleNumber: '', vehicleType: 'Four Wheeler' };

export const VehicleIcon = ({ type, sx }) =>
  type === 'Two Wheeler' ? <TwoWheelerOutlined sx={sx} /> : <DirectionsCarFilledOutlined sx={sx} />;

const FuelEfficiency = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();

  const [tab, setTab] = useState('active');
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const fuelTour = useTour('fuel-efficiency', { ready: !loading });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuVehicle, setMenuVehicle] = useState(null);
  const [confirmVehicle, setConfirmVehicle] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchVehicles = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('vehicles')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_archived', tab === 'archived')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching vehicles:', error);
      notify('Failed to fetch vehicles', 'error');
    } else {
      setVehicles(data || []);
    }
    setLoading(false);
  }, [supabase, user, tab]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // ---- Add / edit ----
  const openAddDialog = () => {
    setEditingVehicle(null);
    setForm(emptyForm);
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (vehicle) => {
    setEditingVehicle(vehicle);
    setForm({ name: vehicle.name, vehicleNumber: vehicle.vehicle_number || '', vehicleType: vehicle.vehicle_type });
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingVehicle(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormError('Please enter vehicle name');
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      vehicle_number: form.vehicleNumber.trim().toUpperCase(),
      vehicle_type: form.vehicleType,
    };
    const { error } = editingVehicle
      ? await supabase.from('vehicles').update(payload).eq('id', editingVehicle.id)
      : await supabase.from('vehicles').insert({ ...payload, user_id: user.id });
    setSaving(false);

    if (error) {
      console.error('Error saving vehicle:', error);
      setFormError(error.code === '23505'
        ? 'This vehicle number already exists'
        : `Failed to ${editingVehicle ? 'update' : 'save'} vehicle`);
      return;
    }

    setDialogOpen(false);
    setEditingVehicle(null);
    setForm(emptyForm);
    notify(editingVehicle ? 'Vehicle updated successfully' : 'Vehicle added successfully');
    if (tab !== 'active' && !editingVehicle) setTab('active');
    else fetchVehicles();
  };

  // ---- Archive / restore ----
  const handleArchiveToggle = async (vehicle) => {
    const archive = !vehicle.is_archived;
    const { error } = await supabase.from('vehicles').update({ is_archived: archive }).eq('id', vehicle.id);
    setConfirmVehicle(null);
    if (error) {
      console.error('Error updating vehicle archive status:', error);
      notify(archive ? 'Failed to archive vehicle' : 'Failed to restore vehicle', 'error');
      return;
    }
    notify(archive ? 'Vehicle archived successfully' : 'Vehicle restored successfully');
    fetchVehicles();
  };

  const openMenu = (event, vehicle) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuVehicle(vehicle);
  };
  const closeMenu = () => setMenuAnchor(null);

  // ---- Render ----
  const emptyState = tab === 'active' ? (
    <Card sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
      <Box sx={{ width: 88, height: 88, borderRadius: '50%', mx: 'auto', mb: 2, display: 'grid', placeItems: 'center', bgcolor: (t) => alpha(t.palette.primary.main, 0.08), color: 'primary.main' }}>
        <DirectionsCarFilledOutlined sx={{ fontSize: 44 }} />
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>No Vehicles Added</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3, maxWidth: 420, mx: 'auto' }}>
        Start tracking your vehicle's fuel efficiency by adding your first vehicle.
      </Typography>
      <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>Add Your First Vehicle</Button>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mt: 5, textAlign: 'left' }}>
        {[
          { icon: <SpeedOutlined />, color: 'primary', title: 'Mileage Tracking', text: 'Monitor km/l for each vehicle' },
          { icon: <CurrencyRupeeOutlined />, color: 'success', title: 'Fuel Cost Analysis', text: 'Track spending and cost per kilometre' },
          { icon: <InsightsOutlined />, color: 'info', title: 'Performance Insights', text: 'See averages across every fill-up' },
        ].map((item) => (
          <Box key={item.title} sx={{ display: 'flex', gap: 1.5, p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
            <Box sx={{ color: `${item.color}.main`, mt: 0.25 }}>{item.icon}</Box>
            <Box>
              <Typography variant="subtitle2">{item.title}</Typography>
              <Typography variant="body2" color="text.secondary">{item.text}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Card>
  ) : (
    <Card sx={{ p: 5, textAlign: 'center' }}>
      <ArchiveOutlined sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>No Archived Vehicles</Typography>
      <Typography variant="body2" color="text.secondary">Archived vehicles will appear here.</Typography>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/utilities')} aria-label="Back to Utilities">
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Fuel Efficiency</Typography>
            <Typography variant="body2" color="text.secondary">Mileage and fuel costs for your vehicles</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TourButton onClick={fuelTour.start} />
          <Button variant="contained" startIcon={<Add />} onClick={openAddDialog} data-tour="fuel-add-vehicle">Add Vehicle</Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(e, value) => setTab(value)} data-tour="fuel-tabs" sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="active" label="Active" />
        <Tab value="archived" label="Archived" icon={<ArchiveOutlined fontSize="small" />} iconPosition="start" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : vehicles.length === 0 ? (
        emptyState
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          {vehicles.map((vehicle) => {
            const archived = vehicle.is_archived;
            const content = (
              <Box sx={{ p: 2.25, display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Box sx={{
                  width: 52, height: 52, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
                  bgcolor: (t) => alpha(archived ? t.palette.text.secondary : t.palette.primary.main, 0.08),
                  color: archived ? 'text.secondary' : 'primary.main',
                }}>
                  <VehicleIcon type={vehicle.vehicle_type} sx={{ fontSize: 28 }} />
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography sx={{ fontWeight: 700 }} noWrap>{vehicle.name}</Typography>
                  {vehicle.vehicle_number && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', letterSpacing: 0.5 }} noWrap>
                      {vehicle.vehicle_number}
                    </Typography>
                  )}
                  <Chip label={vehicle.vehicle_type} size="small" variant="outlined" sx={{ mt: 0.75 }} />
                </Box>
                {!archived && <ChevronRight sx={{ color: 'text.secondary' }} />}
              </Box>
            );

            return (
              <Card key={vehicle.id} data-tour="fuel-vehicle" sx={{ position: 'relative', borderRadius: 3, '&:hover': { boxShadow: archived ? undefined : 'var(--fx-card-shadow-hover)' } }}>
                {archived ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.5 }}>
                    {content}
                    <Button size="small" variant="outlined" startIcon={<UnarchiveOutlined />} onClick={() => handleArchiveToggle(vehicle)} sx={{ flexShrink: 0 }}>
                      Restore
                    </Button>
                  </Box>
                ) : (
                  <>
                    <CardActionArea onClick={() => navigate(`/utilities/fuel-efficiency/${vehicle.id}`)} sx={{ pr: 5 }}>
                      {content}
                    </CardActionArea>
                    <IconButton
                      size="small"
                      onClick={(e) => openMenu(e, vehicle)}
                      aria-label={`Options for ${vehicle.name}`}
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Card>
            );
          })}
        </Box>
      )}

      {/* Vehicle options */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => { closeMenu(); openEditDialog(menuVehicle); }}>
          <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
          Edit Vehicle
        </MenuItem>
        <MenuItem onClick={() => { closeMenu(); setConfirmVehicle(menuVehicle); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><ArchiveOutlined fontSize="small" color="error" /></ListItemIcon>
          Archive Vehicle
        </MenuItem>
      </Menu>

      {/* Add / edit vehicle */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <TextField
              label="Vehicle Name"
              required
              autoFocus
              placeholder="e.g., Honda City, Royal Enfield"
              value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); setFormError(''); }}
            />
            <TextField
              label="Vehicle Number (Optional)"
              placeholder="e.g., MH 01 AB 1234"
              value={form.vehicleNumber}
              onChange={(e) => { setForm({ ...form, vehicleNumber: e.target.value.toUpperCase() }); setFormError(''); }}
            />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Vehicle Type *</Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                color="primary"
                value={form.vehicleType}
                onChange={(e, value) => value && setForm({ ...form, vehicleType: value })}
              >
                {VEHICLE_TYPES.map((type) => (
                  <ToggleButton key={type} value={type} sx={{ py: 1 }}>{type}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingVehicle ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive confirmation */}
      <Dialog open={Boolean(confirmVehicle)} onClose={() => setConfirmVehicle(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Archive Vehicle</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive "{confirmVehicle?.name}"? Its fuel history is kept and you can restore it from the Archived tab.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmVehicle(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleArchiveToggle(confirmVehicle)}>Archive</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <GuidedTour steps={TOURS['fuel-efficiency']} open={fuelTour.open} onClose={fuelTour.close} />
    </Box>
  );
};

export default FuelEfficiency;
